import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { CANVAS_TEXT_SIZES, MAX_CANVAS_TEXT_SIZE, MIN_CANVAS_TEXT_SIZE } from '../utils/canvasText'
import { NumberStepper } from '@/src/renderer/components/ui/NumberStepper'
import type { CanvasOptionsBarPlacement } from './CanvasOptionsBar.types'

export interface CanvasTextSizeMenuProps {
  /** Die aktuelle Größe; `undefined`, wenn die Auswahl uneinheitlich ist. */
  value: number | undefined
  placement: CanvasOptionsBarPlacement
  /** Von außen gemessen, wie beim Schriftmenü (die Leiste prüft, ob er passt). */
  ref?: React.Ref<HTMLDivElement>
  onPick: (size: number) => void
  /**
   * Ein Schritt mit den Pfeilen oder Pfeiltasten des Steppers — die Größe gilt
   * sofort, das Menü bleibt offen und das Feld behält den Fokus.
   */
  onStep?: (size: number) => void
  /** Blendet gerade ab (siehe `usePresence`). */
  leaving?: boolean
  /**
   * Eigene Stufen statt der fünf der Pinnwand — der Projekttitel hat vier
   * kleinere (Klein 16, Mittel 18, Groß 22, Sehr groß 26). `key` benennt die
   * Beschriftung (`projectCanvas.text.size.*`).
   */
  presets?: readonly { key: string; size: number }[]
  /** Der große Schritt (Pfeil-Knöpfe, ⇧↑/⇧↓). Vorgabe 8. */
  step?: number
  /** Beim Öffnen gleich ins Zahlenfeld. */
  autoFocus?: boolean
}

/**
 * Der große Schritt — Pfeil-Knöpfe und ⇧↑/⇧↓: 8, ein Vielfaches des Rasters,
 * auf dem die Stufen liegen (gewünscht 2026-09-23, Knöpfe 2026-09-24).
 */
const STEP = 8

/**
 * Wie groß jede Stufe im Menü steht — steigend wie im Entwurf, aber gedeckelt:
 * „Riesig" in echten 96px sprengte das Menü.
 */
const PREVIEW_SIZES = [14, 16, 18, 21, 24] as const

/**
 * Das **Größen-Menü** des **Texts** (CONTEXT.md, Text; FigJam-Muster): fünf
 * Stufen, jede in ihrer eigenen Größe gesetzt, die Zahl rechts daneben — und
 * darunter der **Zahlen-Stepper** für jede andere Größe (8–400). Die Pfeil-
 * Knöpfe zählen um 8, ↑/↓ im Feld um 1, mit ⇧ um 8 (gewünscht 2026-09-24);
 * Getipptes gilt mit Enter.
 *
 * Das Menü ist die Vorschau wie das Schriftmenü; bei uneinheitlicher Auswahl
 * steht kein Häkchen und das Feld ist leer.
 */
export const CanvasTextSizeMenu: React.FC<CanvasTextSizeMenuProps> = ({
  value, placement, onPick, onStep, ref, leaving = false, presets = CANVAS_TEXT_SIZES, step = STEP, autoFocus = false,
}) => {
  const { t } = useTranslation()
  /*
    Der Stepper zählt vom EIGENEN Stand weiter, nicht von `value`: Das kommt
    erst über die Live-Query zurück, und zwei schnelle Schritte hintereinander
    gingen sonst beide vom alten Wert aus.
  */
  const [size, setSize] = useState<number | null>(value ?? null)
  useEffect(() => { setSize(value ?? null) }, [value])

  return (
    <div
      ref={ref}
      className={`canvas-options-bar__flyout canvas-options-bar__flyout--start canvas-options-bar__flyout--${placement} canvas-text-size-menu`}
      data-leaving={leaving ? '' : undefined}
    >
      <div role="listbox" aria-label={t('projectCanvas.text.changeSize')}>
        {presets.map((preset, index) => {
          const selected = preset.size === value
          return (
            <button
              key={preset.key}
              type="button"
              role="option"
              aria-selected={selected}
              className="canvas-options-bar__font-item canvas-text-size-menu__item"
              onClick={() => onPick(preset.size)}
            >
              {selected && <Check className="canvas-options-bar__font-check" size={16} strokeWidth={2} />}
              <span className="canvas-text-size-menu__label" style={{ fontSize: PREVIEW_SIZES[Math.min(index, PREVIEW_SIZES.length - 1)] }}>
                {t(`projectCanvas.text.size.${preset.key}`)}
              </span>
              <span className="canvas-text-size-menu__value">{preset.size}</span>
            </button>
          )
        })}
      </div>
      <div
        className="canvas-text-size-menu__custom"
        // Die Leiste hält den Fokus sonst fest (`preventDefault` am Mausdruck) —
        // ins Feld muss man klicken können. Die Pfeil-Knöpfe brauchen keinen Fokus.
        onMouseDown={event => { if ((event.target as HTMLElement).tagName === 'INPUT') event.stopPropagation() }}
      >
        <NumberStepper
          className="canvas-text-size-menu__stepper"
          value={size}
          min={MIN_CANVAS_TEXT_SIZE}
          max={MAX_CANVAS_TEXT_SIZE}
          buttonStep={step}
          shiftStep={step}
          autoFocus={autoFocus}
          onValueChange={next => {
            setSize(next)
            onStep?.(next)
          }}
          onValueCommit={onPick}
          aria-label={t('projectCanvas.text.sizeCustom')}
          incrementLabel={t('projectCanvas.text.sizeLarger')}
          decrementLabel={t('projectCanvas.text.sizeSmaller')}
        />
      </div>
    </div>
  )
}
